import { flowRight, isNil, isFunction, partialRight } from 'lodash-es'
import { ColGroupDef, ColDef, ICellRendererParams } from 'ag-grid-community'
import { NegotiationDetail, NegotiationPackageDetail } from 'api'
import {
  TypedValueGetterParams,
  NegotiationPivotedRowCellRenderer,
  defaultCellEditor,
  createCellRenderer,
  isColGroupDef,
  TypedEditableCallbackParams,
  TypedCellClickedEvent,
  isCellRendererClass
} from 'view/components/datagrid'
import { createValueGetterCounterpartyDataMapper } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { NoopCellEditor } from 'view/components/datagrid/renderers/NoopCellEditor'
import { shouldStartEdit } from 'view/routes/requests/shared/utils'

const COUNTER_PARTY_DISPLAYED_TAB_COUNT = 8

export type ValueGetterMapper = <
  T extends
    | TypedValueGetterParams<NegotiationPackageDetail, NegotiationDetail>
    | TypedCellClickedEvent<NegotiationPackageDetail, NegotiationDetail>
    | (Omit<ICellRendererParams, 'data'> & { data: NegotiationPackageDetail })
>(
  params: T
) => T

const getCounterpartyColId = (colId: string, counterpartyId: string) => `${colId}_${counterpartyId}`

export const getNegotiationId = ({ negotiation }: NegotiationPackageDetail | NegotiationDetail) => negotiation.negotiationId

type ColumnIdGetter = (colId: string) => string

export const getHeaderComponentParams = (
  { headerComponentParams, colId }: ColDef,
  columnIdGetter: ColumnIdGetter
): ColDef['headerComponentParams'] => {
  if (isNil(headerComponentParams)) {
    return headerComponentParams
  }

  const children = headerComponentParams.children && headerComponentParams.children.map((columnId: string) => columnIdGetter(columnId))
  const parent = headerComponentParams.parent && colId && columnIdGetter(headerComponentParams.parent)

  return {
    ...headerComponentParams,
    children,
    parent
  }
}

export const getCellEditorParams = ({ cellEditorParams }: ColDef, paramOptions: Record<string, string>): ColDef['cellEditorParams'] => ({
  ...cellEditorParams,
  getNodeId: getNegotiationId,
  ...paramOptions
})

export const getCellRendererParams = (
  { cellRendererParams, cellRenderer: targetCellRenderer, colId }: ColDef,
  valueMapper: ValueGetterMapper,
  paramOptions?: Record<string, string>
): ColDef['cellRendererParams'] => ({
  ...cellRendererParams,
  originalColId: colId,
  valueMapper,
  targetCellRenderer,
  ...paramOptions
})

const createValueGetter = (originalGetter: Function) => (params: TypedValueGetterParams<NegotiationDetail | undefined>) =>
  params.data && originalGetter(params)

export const createGetValueGetters = (valueGetterDataMapper: ValueGetterMapper) => (
  colDef: ColDef
): Pick<ColDef, 'valueGetter' | 'filterValueGetter' | 'getQuickFilterText' | 'cellClass'> => {
  const valueGetters = ['valueGetter', 'filterValueGetter', 'getQuickFilterText', 'cellClass']
  return valueGetters.reduce(
    (result, valueGetter: keyof ColDef) => ({
      ...result,
      ...(colDef[valueGetter] && {
        [valueGetter]: flowRight(createValueGetter(colDef[valueGetter]), valueGetterDataMapper)
      })
    }),
    {}
  )
}

export const getEditable = ({ editable }: ColDef, valueGetterMapper: ValueGetterMapper): ColDef['editable'] =>
  editable &&
  flowRight((params: TypedEditableCallbackParams<NegotiationPackageDetail>) => {
    if (isFunction(editable) && !editable(params)) {
      return false
    }
    return shouldStartEdit(params.data)
  }, valueGetterMapper)

export const getFrameworkRenderers = (colDef: ColDef, valueGetterMapper: ValueGetterMapper) => {
  const cellRenderer =
    colDef.cellRenderer && !isCellRendererClass(colDef.cellRenderer) && createCellRenderer(colDef.cellRenderer, { valueGetterMapper })

  const cellEditor = createCellRenderer(colDef.cellEditor || defaultCellEditor, {
    FallbackComponent: NoopCellEditor as any,
    valueGetterMapper
  })

  const headerComponent =
    colDef.headerComponent &&
    createCellRenderer(colDef.headerComponent, {
      forceComponent: true,
      valueGetterMapper
    })

  return {
    cellEditor,
    cellRenderer,
    headerComponent
  }
}

const getCellRenderer = ({ cellRenderer }: ColDef) => cellRenderer && isCellRendererClass(cellRenderer) && NegotiationPivotedRowCellRenderer

export const createMapCounterpartyColumns = (counterpartyId: string) => {
  const defaultBaseColDef: ColDef = {
    enableRowGroup: false,
    rowGroup: false
  }

  return <T extends ColGroupDef | ColDef>(counterpartyColDef: T, index: number): T => {
    const columnGroupShow = index >= COUNTER_PARTY_DISPLAYED_TAB_COUNT ? 'open' : undefined

    if (isColGroupDef(counterpartyColDef)) {
      const mapCounterpartyColumns = createMapCounterpartyColumns(counterpartyId)
      const children = counterpartyColDef.children.map(mapCounterpartyColumns)

      return {
        ...counterpartyColDef,
        columnGroupShow,
        children
      }
    }

    const valueGetterCounterpartyDataMapper = createValueGetterCounterpartyDataMapper(counterpartyId)
    const partialGetCounterpartyColId = partialRight(getCounterpartyColId, counterpartyId)

    const colId = counterpartyColDef.colId && getCounterpartyColId(counterpartyColDef.colId, counterpartyId)
    const headerComponentParams = getHeaderComponentParams(counterpartyColDef, partialGetCounterpartyColId)
    const editable = getEditable(counterpartyColDef, valueGetterCounterpartyDataMapper)
    const cellEditorParams = getCellEditorParams(counterpartyColDef, { counterpartyId })
    const frameworkRenderers = getFrameworkRenderers(counterpartyColDef, valueGetterCounterpartyDataMapper)
    const headerClass = `${counterpartyColDef.headerClass || ''} header-cell--grouped-col`
    const valueGetters = createGetValueGetters(valueGetterCounterpartyDataMapper)(counterpartyColDef)

    const cellRendererParams = getCellRendererParams(counterpartyColDef, valueGetterCounterpartyDataMapper, { counterpartyId })
    const cellRenderer = getCellRenderer(counterpartyColDef)

    return {
      ...counterpartyColDef,
      ...defaultBaseColDef,
      ...valueGetters,
      cellEditorParams,
      headerComponent: frameworkRenderers.headerComponent,
      cellEditor: frameworkRenderers.cellEditor,
      cellRenderer: frameworkRenderers.cellRenderer || cellRenderer,
      cellRendererParams,
      colId,
      columnGroupShow,
      editable,
      headerClass,
      headerComponentParams
    }
  }
}
